import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    FileText, Layers3, Monitor, CalendarDays, Info, ChartNoAxesColumnIncreasing,
    ExternalLink, Copy, Check, Search, ListFilter, ChevronDown, Download,
    Power, RefreshCw, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { http, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STATUS_TONE = {
    active: "bg-success/10 text-success",
    trialing: "bg-sky-500/10 text-sky-600 dark:text-sky-300",
    past_due: "bg-warning/10 text-warning",
    suspended: "bg-destructive/10 text-destructive",
    canceled: "bg-muted text-muted-foreground",
    cancelled: "bg-muted text-muted-foreground",
};

const KIND_LABEL = {
    desktop_app: "Desktop App",
    pc: "PC",
    server: "Server",
    nas: "NAS",
    other: "Other",
};

const ROLE_RANK = { viewer: 1, auditor: 2, engineer: 3, admin: 4, superadmin: 5 };

function fmtDate(iso) {
    if (!iso) return null;
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function fmtDateTime(iso) {
    if (!iso) return null;
    return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

export default function PortalLicenses() {
    const { user } = useAuth();
    const canManage = (ROLE_RANK[user?.role] || 0) >= ROLE_RANK.engineer;
    const canBill = user?.role === "admin" || user?.role === "superadmin";

    const [license, setLicense] = useState(null);
    const [licenseErr, setLicenseErr] = useState("");
    const [devices, setDevices] = useState(null);
    const [devicesErr, setDevicesErr] = useState("");
    const [team, setTeam] = useState(null);
    const [portalBusy, setPortalBusy] = useState(false);

    const loadLicense = async () => {
        try {
            const { data } = await http.get("/licensing/license");
            setLicense(data);
            setLicenseErr("");
        } catch (e) {
            setLicenseErr(errMsg(e));
        }
    };

    const loadDevices = async () => {
        try {
            const { data } = await http.get("/licensing/devices", { params: { status: "all" } });
            setDevices(data.devices || []);
            setDevicesErr("");
        } catch (e) {
            setDevicesErr(errMsg(e));
        }
    };

    useEffect(() => {
        loadLicense();
        loadDevices();
        (async () => {
            try {
                const { data } = await http.get("/users");
                setTeam(data || []);
            } catch {
                setTeam(null); // admin/auditor-only -- "Activated by" cells show "—" instead
            }
        })();
    }, []);

    const openBillingPortal = async () => {
        setPortalBusy(true);
        try {
            const { data } = await http.post("/billing/portal", { return_url: window.location.href });
            window.location.href = data.url;
        } catch (e) {
            toast.error(errMsg(e));
        } finally {
            setPortalBusy(false);
        }
    };

    if (licenseErr) {
        return <div className="p-6 text-sm text-destructive" data-testid="portal-licenses-error">{licenseErr}</div>;
    }
    if (!license) {
        return (
            <div className="flex items-center justify-center p-16 text-muted-foreground" data-testid="portal-licenses-loading">
                <Loader2 size={16} className="mr-2 animate-spin" /> Loading your license…
            </div>
        );
    }

    const seats = license.seats || {};
    const unlimited = seats.max_devices == null;
    const tone = STATUS_TONE[license.status] || "bg-muted text-muted-foreground";

    return (
        <div className="p-6 sm:p-9" data-testid="portal-licenses-page">
            <div className="mb-5 flex items-start justify-between gap-4">
                <div>
                    <div className="text-xs font-medium text-muted-foreground">Licenses</div>
                    <h1 className="mt-1.5 font-display text-[26px] font-bold leading-tight tracking-tight sm:text-[28px]">
                        {license.plan_label}
                    </h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                        Manage your license, view usage, and adjust your plan settings.
                    </p>
                </div>
                {canBill && (
                    <button
                        onClick={openBillingPortal}
                        disabled={portalBusy}
                        data-testid="portal-licenses-manage-btn"
                        aria-label="Manage plan and billing"
                        className="inline-flex h-9 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md bg-brand px-4 text-[11.5px] font-semibold text-white transition-colors hover:bg-brand-dark disabled:opacity-50"
                    >
                        <ExternalLink size={14} /> {portalBusy ? "Opening…" : "Manage Plan / Billing"}
                    </button>
                )}
            </div>

            <MetricGrid license={license} seats={seats} unlimited={unlimited} tone={tone} />

            <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,2.2fr)_minmax(300px,1fr)]">
                <LicenseDetailsCard license={license} />
                <UsageOverviewCard license={license} seats={seats} unlimited={unlimited} />
            </div>

            <ActivatedDevicesCard
                devices={devices}
                err={devicesErr}
                team={team}
                canManage={canManage}
                onChanged={loadDevices}
            />
        </div>
    );
}

/* ─────────────────────────── Metric grid ─────────────────────────── */

function MetricCard({ icon: Icon, label, children, testId }) {
    return (
        <div
            className="flex min-h-[116px] gap-4 rounded-xl border border-border bg-surface p-[18px] shadow-[0_1px_2px_rgba(16,24,40,0.02)]"
            data-testid={testId}
        >
            <div className="flex h-[44px] w-[44px] shrink-0 items-center justify-center rounded-[11px] bg-brand/10 text-brand">
                <Icon size={20} />
            </div>
            <div className="min-w-0 flex-1">
                <div className="text-[13px] font-medium text-muted-foreground">{label}</div>
                {children}
            </div>
        </div>
    );
}

function MetricGrid({ license, seats, unlimited, tone }) {
    const pct = !unlimited && seats.max_devices ? Math.min(100, Math.round((seats.used / seats.max_devices) * 100)) : 0;
    const days = license.current_period_end
        ? Math.max(0, Math.ceil((new Date(license.current_period_end).getTime() - Date.now()) / 86400000))
        : null;

    return (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4" data-testid="portal-licenses-metric-grid">
            <MetricCard icon={FileText} label="Plan" testId="portal-licenses-metric-plan">
                <div className="mt-0.5 truncate font-display text-[22px] font-bold leading-tight">{license.plan_label}</div>
                <span className={`mt-1.5 inline-flex h-5 items-center rounded-full px-2 text-[10.5px] font-semibold capitalize ${tone}`}>
                    {(license.status || "—").replace("_", " ")}
                </span>
            </MetricCard>

            <MetricCard icon={Layers3} label="Total Licenses" testId="portal-licenses-metric-total">
                <div className="mt-0.5 font-display text-[21px] font-bold leading-tight">
                    {unlimited ? "∞ Unlimited" : seats.max_devices}
                </div>
                <div className="mt-1.5 text-xs text-muted-foreground">{unlimited ? "No limit" : "Licenses purchased"}</div>
            </MetricCard>

            {/* "License Seats", not "Devices" -- this counts Desktop app
                license activations (license_activations), a completely
                different count from the migration dashboard's own "fleet
                devices" (db.agents, includes storage connections) metric,
                which happens to share the same plan cap number and would
                otherwise look like the same thing. */}
            <MetricCard icon={Monitor} label="License Seats" testId="portal-licenses-metric-devices">
                <div className="mt-0.5 font-display text-[21px] font-bold leading-tight tabular-nums">
                    {seats.used ?? 0} / {unlimited ? "∞" : seats.max_devices}
                </div>
                <div className="mt-2 h-[7px] w-full max-w-[200px] overflow-hidden rounded-full bg-[hsl(var(--muted))]">
                    <div className="h-full rounded-full bg-brand transition-[width]" style={{ width: `${unlimited ? (seats.used ? 100 : 0) : pct}%` }} />
                </div>
                <div className="mt-1.5 text-xs text-muted-foreground">{unlimited ? "0% in use" : `${pct}% in use`}</div>
            </MetricCard>

            <MetricCard icon={CalendarDays} label="Renewal Date" testId="portal-licenses-metric-renewal">
                <div className="mt-0.5 font-display text-[19px] font-bold leading-tight">
                    {license.current_period_end ? fmtDate(license.current_period_end) : "No renewal scheduled"}
                </div>
                <div className="mt-1.5 text-xs text-muted-foreground">
                    {days != null ? `in ${days} day${days === 1 ? "" : "s"}` : `${license.plan_label} account`}
                </div>
            </MetricCard>
        </div>
    );
}

/* ─────────────────────────── License details ─────────────────────── */

function DetailRow({ label, value, last }) {
    return (
        <div className={"grid grid-cols-[180px_1fr] items-center py-2.5" + (last ? "" : " border-b border-border")}>
            <div className="text-[10.5px] font-medium text-muted-foreground">{label}</div>
            <div className="truncate text-[11.5px] font-medium">{value}</div>
        </div>
    );
}

function LicenseDetailsCard({ license }) {
    const [copied, setCopied] = useState(false);

    const copyRef = async () => {
        try {
            await navigator.clipboard.writeText(license.reference_id || "");
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        } catch {
            toast.error("Clipboard unavailable — copy manually");
        }
    };

    // §76: a null renewal date means there's no real recurring-renewal
    // mechanism for this account (e.g. a manually-provisioned Enterprise
    // contract with no Stripe subscription) -- showing "Auto-renew: Yes"
    // next to "Renewal date: —" would contradict itself, so normalize
    // auto-renew to "Not applicable" in that state rather than always
    // defaulting to Yes.
    const autoRenew = !license.has_stripe_subscription
        ? "Not applicable"
        : license.cancel_at_period_end
            ? "Cancels at period end"
            : "Yes";
    const billingType = license.has_stripe_subscription
        ? (license.status === "trialing" ? "Trial (Stripe)" : "Self-serve (Stripe)")
        : `${license.plan_label} (Managed)`;

    return (
        <div className="rounded-xl border border-border bg-surface" data-testid="portal-licenses-details">
            <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3.5 sm:px-[18px]">
                <div className="flex items-center gap-3">
                    <div className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[10px] bg-brand/10 text-brand">
                        <Info size={17} />
                    </div>
                    <div>
                        <div className="text-[14.5px] font-bold">License Details</div>
                        <div className="text-[11px] text-muted-foreground">Key information about your current license.</div>
                    </div>
                </div>
                <a
                    href="https://moovbytes.com/legal/license-agreement"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex h-[34px] shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md border border-brand/40 px-3 text-[11px] font-semibold text-brand transition-colors hover:bg-brand/5"
                >
                    View license agreement <ExternalLink size={13} />
                </a>
            </div>
            <div className="grid grid-cols-1 gap-x-8 px-4 sm:grid-cols-2 sm:px-[18px]">
                <div>
                    <div className="flex items-center justify-between border-b border-border py-2.5">
                        <div className="text-[10.5px] font-medium text-muted-foreground">License key</div>
                        <button
                            onClick={copyRef}
                            aria-label="Copy license reference"
                            data-testid="portal-licenses-copy-ref"
                            className="flex items-center gap-1.5 text-[12px] font-semibold hover:text-brand"
                        >
                            <span className="font-mono">{license.reference_id}</span>
                            {copied ? <Check size={13} className="text-success" /> : <Copy size={13} />}
                        </button>
                    </div>
                    <DetailRow label="Plan" value={license.plan_label} />
                    <DetailRow label="License seats" value={`${license.seats?.used ?? 0} / ${license.seats?.max_devices ?? "Unlimited"}`} />
                    <DetailRow label="Concurrency cap" value={license.concurrency_cap ?? "Unlimited"} />
                    <DetailRow
                        last
                        label="Allowed device types"
                        value={
                            <div className="flex flex-wrap gap-1">
                                {(license.allow_kinds || []).length
                                    ? license.allow_kinds.map((k) => (
                                        <span key={k} className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold">
                                            {KIND_LABEL[k] || k}
                                        </span>
                                    ))
                                    : "—"}
                            </div>
                        }
                    />
                </div>
                <div>
                    <DetailRow label="Renewal date" value={license.current_period_end ? fmtDate(license.current_period_end) : "Not scheduled"} />
                    <DetailRow label="Auto-renew" value={autoRenew} />
                    <DetailRow label="Billing type" value={billingType} />
                    <DetailRow label="Issued" value={license.issued_at ? fmtDate(license.issued_at) : "—"} />
                    <DetailRow last label="Organization" value={license.org_name || "—"} />
                </div>
            </div>
        </div>
    );
}

/* ─────────────────────────── Usage overview ───────────────────────── */

function UsageOverviewCard({ license, seats, unlimited }) {
    const used = seats.used ?? 0;
    const cap = seats.max_devices;
    const pct = unlimited ? 0 : cap ? Math.min(100, Math.round((used / cap) * 100)) : 0;

    const size = 116;
    const stroke = 9;
    const r = (size - stroke) / 2;
    const c = 2 * Math.PI * r;
    const showCount = unlimited && used > 0;

    return (
        <div className="rounded-xl border border-border bg-surface p-4 sm:p-[18px]" data-testid="portal-licenses-usage">
            <div className="flex items-center gap-3">
                <div className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[10px] bg-brand/10 text-brand">
                    <ChartNoAxesColumnIncreasing size={17} />
                </div>
                <div>
                    <div className="text-[14.5px] font-bold">Usage Overview</div>
                    <div className="text-[11px] text-muted-foreground">Current license usage and limits.</div>
                </div>
            </div>

            <div className="mt-4 flex justify-center">
                <div className="relative" style={{ width: size, height: size }}>
                    <svg width={size} height={size}>
                        <circle cx={size / 2} cy={size / 2} r={r} stroke="hsl(var(--muted))" strokeWidth={stroke} fill="none" />
                        {!showCount && (
                            <circle
                                cx={size / 2} cy={size / 2} r={r}
                                stroke="hsl(var(--primary))" strokeWidth={stroke} fill="none"
                                strokeLinecap="round"
                                strokeDasharray={c}
                                strokeDashoffset={c - (c * pct) / 100}
                                style={{ transform: "rotate(-90deg)", transformOrigin: "50% 50%" }}
                            />
                        )}
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <div className="font-display text-lg font-bold leading-none">{showCount ? used : `${pct}%`}</div>
                        <div className="mt-1 text-[10px] text-muted-foreground">{showCount ? "Active" : "Used"}</div>
                    </div>
                </div>
            </div>

            <div className="mt-4 space-y-2 text-xs">
                <div className="flex items-center justify-between">
                    <span className="flex items-center gap-1.5 text-muted-foreground">
                        <span className="h-2 w-2 rounded-full bg-brand" /> Activated seats
                    </span>
                    <span className="font-semibold">{used}</span>
                </div>
                <div className="flex items-center justify-between">
                    <span className="flex items-center gap-1.5 text-muted-foreground">
                        <span className="h-2 w-2 rounded-full bg-muted-foreground" /> Available licenses
                    </span>
                    <span className="font-semibold">{unlimited ? "∞" : seats.available ?? Math.max(0, (cap || 0) - used)}</span>
                </div>
            </div>

            {unlimited ? (
                <div className="mt-4 flex items-start gap-2 rounded-lg bg-brand/10 p-2.5 text-[10.5px] text-brand">
                    <Info size={14} className="mt-px shrink-0" />
                    Your {license.plan_label} plan includes unlimited licenses. Activate as many devices as you need.
                </div>
            ) : (
                <div className="mt-4 text-[11px] text-muted-foreground">
                    {Math.max(0, (cap || 0) - used)} of {cap} activations available.
                </div>
            )}
        </div>
    );
}

/* ───────────────────────── Activated devices ──────────────────────── */

const STATUS_FILTERS = [
    { v: "all", label: "All statuses" },
    { v: "active", label: "Active" },
    { v: "deactivated", label: "Deactivated" },
];

function ActivatedDevicesCard({ devices, err, team, canManage, onChanged }) {
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [kindFilter, setKindFilter] = useState("all");
    const [showFilters, setShowFilters] = useState(false);
    const [busyId, setBusyId] = useState(null);

    const teamByEmail = useMemo(() => {
        if (!team) return null;
        const m = new Map();
        for (const t of team) m.set(t.id, t.email);
        return m;
    }, [team]);

    const kinds = useMemo(
        () => Array.from(new Set((devices || []).map((d) => d.kind))).filter(Boolean),
        [devices],
    );

    const filtered = useMemo(() => {
        let rows = devices || [];
        if (statusFilter !== "all") rows = rows.filter((d) => d.status === statusFilter);
        if (kindFilter !== "all") rows = rows.filter((d) => d.kind === kindFilter);
        const q = search.trim().toLowerCase();
        if (q) rows = rows.filter((d) => (d.device_name || "").toLowerCase().includes(q));
        return rows;
    }, [devices, statusFilter, kindFilter, search]);

    const deactivate = async (id) => {
        if (!window.confirm("Deactivate this device? This will release one license activation.")) return;
        setBusyId(id);
        try {
            await http.post(`/licensing/devices/${id}/deactivate`);
            toast.success("Device deactivated.");
            onChanged();
        } catch (e) {
            toast.error(errMsg(e));
        } finally {
            setBusyId(null);
        }
    };

    const reactivate = async (d) => {
        setBusyId(d.id);
        try {
            await http.post("/licensing/activate", {
                device_name: d.device_name,
                device_fingerprint: d.device_fingerprint,
                kind: d.kind,
            });
            toast.success("Device reactivated.");
            onChanged();
        } catch (e) {
            toast.error(errMsg(e));
        } finally {
            setBusyId(null);
        }
    };

    return (
        <div className="mt-4 rounded-xl border border-border bg-surface" data-testid="portal-licenses-devices">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3.5 sm:px-[18px]">
                <div className="flex items-center gap-3">
                    <div className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[10px] bg-brand/10 text-brand">
                        <Monitor size={17} />
                    </div>
                    <div>
                        <div className="text-[14.5px] font-bold">License Seats</div>
                        <div className="text-[11px] text-muted-foreground">Desktop activations currently using your license.</div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <div className="relative">
                        <Search size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search devices…"
                            aria-label="Search activated devices"
                            data-testid="portal-licenses-search"
                            className="h-9 w-[220px] rounded-md border border-input bg-background pl-8 pr-3 text-[11.5px] outline-none focus:border-brand/50"
                        />
                    </div>
                    <div className="relative">
                        <button
                            onClick={() => setShowFilters((v) => !v)}
                            data-testid="portal-licenses-filters-btn"
                            className="flex h-9 items-center gap-1.5 rounded-md border border-brand/40 px-3 text-[11.5px] font-semibold text-brand hover:bg-brand/5"
                        >
                            <ListFilter size={14} /> Filters <ChevronDown size={12} />
                        </button>
                        {showFilters && (
                            <div className="absolute right-0 top-full z-10 mt-1.5 w-56 rounded-lg border border-border bg-surface p-3 shadow-xl">
                                <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Status</div>
                                <select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    className="mt-1 h-8 w-full rounded-md border border-input bg-background px-2 text-[11.5px]"
                                >
                                    {STATUS_FILTERS.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
                                </select>
                                <div className="mt-2.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Device type</div>
                                <select
                                    value={kindFilter}
                                    onChange={(e) => setKindFilter(e.target.value)}
                                    className="mt-1 h-8 w-full rounded-md border border-input bg-background px-2 text-[11.5px]"
                                >
                                    <option value="all">All types</option>
                                    {kinds.map((k) => <option key={k} value={k}>{KIND_LABEL[k] || k}</option>)}
                                </select>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {err && <div className="p-4 text-sm text-destructive">{err}</div>}

            {!err && devices === null && (
                <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground">
                    <Loader2 size={14} className="animate-spin" /> Loading devices…
                </div>
            )}

            {!err && devices !== null && filtered.length === 0 && (
                <div className="flex flex-col items-center justify-center gap-2 py-16 text-center" data-testid="portal-licenses-devices-empty">
                    <div className="flex h-11 w-11 items-center justify-center rounded-[11px] bg-brand/10 text-brand">
                        <Monitor size={20} />
                    </div>
                    <div className="text-[13.5px] font-semibold">
                        {devices.length === 0 ? "No devices activated yet" : "No devices match your filters"}
                    </div>
                    <p className="max-w-[320px] text-[11.5px] text-muted-foreground">
                        {devices.length === 0
                            ? "Download MoovBytes and activate your first device to see it here."
                            : "Try a different search term or clear your filters."}
                    </p>
                    {devices.length === 0 && (
                        <Link
                            to="/portal/downloads"
                            className="mt-1 inline-flex h-9 items-center gap-1.5 rounded-md bg-brand px-4 text-[11.5px] font-semibold text-white hover:bg-brand-dark"
                        >
                            <Download size={14} /> Download MoovBytes
                        </Link>
                    )}
                </div>
            )}

            {!err && filtered.length > 0 && (
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs" data-testid="portal-licenses-devices-table">
                        <thead>
                            <tr className="bg-[hsl(var(--muted))]/40 text-[9.5px] font-semibold uppercase tracking-[0.03em] text-muted-foreground">
                                <th className="px-4 py-2 sm:px-[18px]">Device</th>
                                <th className="px-2 py-2">Type</th>
                                <th className="px-2 py-2">Activated by</th>
                                <th className="px-2 py-2">Last seen</th>
                                <th className="px-2 py-2">Status</th>
                                {canManage && <th className="px-4 py-2 text-right sm:px-[18px]">Action</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((d) => (
                                <tr key={d.id} className="border-t border-border" data-testid={`portal-licenses-device-row-${d.id}`}>
                                    <td className="px-4 py-2.5 font-semibold sm:px-[18px]">{d.device_name}</td>
                                    <td className="px-2 py-2.5 text-muted-foreground">{KIND_LABEL[d.kind] || d.kind}</td>
                                    <td className="px-2 py-2.5 text-muted-foreground">
                                        {teamByEmail?.get(d.activated_by_user_id) || "—"}
                                    </td>
                                    <td className="px-2 py-2.5 text-muted-foreground">
                                        {d.last_seen_at ? fmtDateTime(d.last_seen_at) : "—"}
                                    </td>
                                    <td className="px-2 py-2.5">
                                        <span className={
                                            "inline-flex h-[21px] items-center rounded-full px-2 text-[10px] font-semibold " +
                                            (d.status === "active" ? "bg-success/10 text-success" : "bg-muted text-muted-foreground")
                                        }>
                                            {d.status === "active" ? "Active" : "Deactivated"}
                                        </span>
                                    </td>
                                    {canManage && (
                                        <td className="px-4 py-2.5 text-right sm:px-[18px]">
                                            {d.status === "active" ? (
                                                <button
                                                    onClick={() => deactivate(d.id)}
                                                    disabled={busyId === d.id}
                                                    data-testid={`portal-licenses-deactivate-${d.id}`}
                                                    className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-[10px] font-semibold uppercase tracking-wide hover:border-destructive hover:text-destructive disabled:opacity-50"
                                                >
                                                    <Power size={10} /> Deactivate
                                                </button>
                                            ) : (
                                                <button
                                                    onClick={() => reactivate(d)}
                                                    disabled={busyId === d.id}
                                                    data-testid={`portal-licenses-reactivate-${d.id}`}
                                                    className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-[10px] font-semibold uppercase tracking-wide hover:border-success hover:text-success disabled:opacity-50"
                                                >
                                                    <RefreshCw size={10} /> Reactivate
                                                </button>
                                            )}
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
